import type { z } from "zod/v4";
import type { GetBopForTrackerTableQuery } from "../generated/gql/graphql";
import type { BopCarBop } from "../generated/graphql";
import { plainBaseClasses } from "../utils";
import { getCellColorBopTracker } from "./cell-colour";
import type {
  FakeAgTableColDefinition,
  FakeAgTableCellDefinition,
  fakeAgTableSchema,
} from "../fake-ag-table-for-report-schema";
import { groupByCarSpec } from "./row-data-grouping";
import { BopFieldLabelConfig, type bopFieldKey, type BopFieldValue } from "./field-labels";
import { getContrastYIQ } from "../get-contrast-yiq";

export const generateBopTrackerCols = (
  sessions: GetBopForTrackerTableQuery["sessions"],
): FakeAgTableColDefinition[] => {
  const columns: FakeAgTableColDefinition[] = [{ label: "", columnWidth: "w-40" }];

  sessions?.items?.forEach(session => {
    // Presuming that the bop name is the desired column header
    // The text should be vertical but thats not really a web practice

    columns.push({
      label: session.bop?.name ? `${session.name}: ${session.bop?.name}` : session.name,
      align: "text-center",
    });
  });

  return columns;
};

export type BopFieldSection = {
  id: string;
  label: string;
  fields: string[];
};

export const generateBopTrackerRows = (
  sessions: GetBopForTrackerTableQuery["sessions"],
  sections: BopFieldSection[],
  isBopTracker: boolean = false,
) => {
  const extraStyle = isBopTracker ? `${plainBaseClasses.backgroundColor} ${plainBaseClasses.textColor}` : "";

  const rows: FakeAgTableCellDefinition[][] = [];

  const sessionCount = sessions?.items?.length || 0;
  const groupedCarSpecs = groupByCarSpec(sessions);

  sections.forEach(section => {
    section.fields?.forEach(fieldKey => {
      // blank divider row
      rows.push([
        {
          value: "",
          colSpan: sessionCount + 1,
          cellStyle: `${extraStyle} h-4`,
        },
      ]);

      let fieldKeyLabel = fieldKey;
      const field = BopFieldLabelConfig?.[fieldKey as bopFieldKey];
      if (field) {
        fieldKeyLabel = field?.unit ? `${field.label} [${field.unit}]` : field.label;
      }

      // field section header row
      rows.push([
        {
          value: fieldKeyLabel,
          colSpan: sessionCount + 1,
          cellStyle: `px-2 py-0.5 font-semibold ${extraStyle} text-sm`,
        },
      ]);

      // car manufacturer bop data group
      groupedCarSpecs.forEach((data, carSpec) => {
        const highlightColour = data.highlightColour;
        const row: FakeAgTableCellDefinition[] = [
          {
            backgroundColor: highlightColour,
            textColor: highlightColour
              ? getContrastYIQ(highlightColour, "hard") === "white"
                ? "#ffffff"
                : "#000000"
              : undefined,
            value: carSpec,
            cellStyle: `px-1 py-0.5 ${extraStyle} text-center text-xs`,
          },
        ];

        sessions?.items?.forEach((session, sessionIndex) => {
          const carBop = data.sessions.get(session._id);

          let value = carBop?.[fieldKey as keyof BopCarBop];

          // pEngineBoost is an object so need to know what to show in the table?
          if ((fieldKey as keyof BopCarBop) === "pEngineBoost") {
            value = undefined;
          }

          const prevValue =
            sessionIndex > 0
              ? data.sessions.get(sessions?.items?.[sessionIndex - 1]?._id || "")?.[fieldKey as keyof BopCarBop]
              : undefined;

          const backgroundColor =
            sessionIndex > 0 ? (getCellColorBopTracker(value, prevValue, fieldKey) ?? undefined) : undefined;

          row.push({
            backgroundColor,
            textColor: backgroundColor
              ? getContrastYIQ(backgroundColor, "hard") === "white"
                ? "#ffffff"
                : "#000000"
              : undefined,
            value: formatFieldDisplayValue(value),
            cellStyle: `px-1 py-0.5 ${extraStyle} text-center text-xs`,
          });
        });

        rows.push(row);
      });
    });
  });

  return rows;
};

const formatFieldDisplayValue = (value: BopFieldValue | undefined) =>
  value != null && value !== "" ? String(value) : "N/A";

export function generateBopTrackerTableById(
  sessions: GetBopForTrackerTableQuery["sessions"],
  tabs: { id: string; config: { selectedBopFields?: string[] } }[],
): Record<string, z.infer<typeof fakeAgTableSchema>> {
  if (!sessions?.items?.length || !tabs.length) return {};
  return Object.fromEntries(
    tabs
      .filter(tab => tab.id && tab.config.selectedBopFields?.length)
      .map(tab => {
        const sections = (tab.config.selectedBopFields || []).map(v => ({
          label: v,
          id: v,
          fields: [v],
        }));

        return [
          tab.id,
          {
            cols: generateBopTrackerCols(sessions),
            rows: generateBopTrackerRows(sessions, sections, true),
          },
        ];
      }),
  );
}
